import { Component, effect, HostListener, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { FileUtils } from '@bn/primitives';
import type { ExtendedVideo } from '@bn/shared/core/api';

import { ButtonComponent } from '../../../button/button.component';
import { MarkAllTouchedDirective } from '../../../directives/mark-all-touched.directive';
import { UploadFileComponent } from '../../../upload-file/upload-file.component';
import { AbstractDialogDirective } from '../../abstracts/abstract-dialog.directive';
import { ModalState } from '../../models/modal-state.model';

@Component({
  selector: 'bn-web-ui-upload-files-dialog',
  templateUrl: './upload-files-dialog.component.html',
  imports: [FaIconComponent, FormsModule, MarkAllTouchedDirective, ReactiveFormsModule, UploadFileComponent, ButtonComponent],
})
export class UploadFilesDialogComponent extends AbstractDialogDirective {
  bsModalRef = inject(BsModalRef);

  #fb = inject(NonNullableFormBuilder);
  #translate = inject(TranslateService);

  uploadFilesForm = this.#fb.group({
    files: this.#fb.control<File[]>([], [Validators.required]),
  });
  allowedFileTypes = this.appConfig.supportedUploadFileTypes.video;
  // TODO: clean up to do it via selected storage store, so we don't need to provide tokens here
  readonly extra: ModalState['extra'] = {};
  readonly metadata?: ModalState['metadata'];
  readonly existingItem?: ModalState['item'];

  #formChanges = toSignal(this.uploadFilesForm.valueChanges);

  constructor() {
    super();

    effect(() => {
      const files = this.#formChanges()?.files;
      if (files) this.onUploadCallback(files);
    });
  }

  @HostListener('document:dragenter', ['$event'])
  onDocDragEnter(event: DragEvent): void {
    if (!this.#hasFiles(event)) return;
    event.preventDefault();
  }

  @HostListener('document:dragover', ['$event'])
  onDocDragOver(event: DragEvent): void {
    if (!this.#hasFiles(event)) return;
    event.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  onDocDrop(event: DragEvent): void {
    if (!this.#hasFiles(event)) return;

    if (event.defaultPrevented) return;

    event.preventDefault();
    this.#handleDroppedFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  #hasFiles(event: DragEvent): boolean {
    return !!event.dataTransfer?.types?.includes('Files');
  }

  #handleDroppedFiles(dropped: File[]): void {
    if (!dropped.length) return;

    const allowed = this.allowedFileTypes ?? [];
    const valid = dropped.filter((file) => !allowed.length || allowed.includes(FileUtils.getFileExtension(file.name).toLowerCase()));

    if (!valid.length) {
      this.toastr.danger('File upload error', "No files were uploaded since one or more files don't match allowed file types.");
      return;
    }

    if (valid.length !== dropped.length) {
      this.toastr.warning('Partial files upload', 'Only some of files were uploaded since not all match allowed file types.');
    }

    this.onUploadCallback(valid);
  }

  private onUploadCallback(files: File[]): void {
    if (!this.callbacks) throw new Error(this.#translate.instant('ERROR.FILE-UPLOAD-HANDLERS'));

    const { storageFileUpload, videoFileUpload } = this.callbacks;

    // start upload tasks
    for (const file of files) {
      // TODO: clean up to do it via selected storage store, so we don't need to provide tokens here
      if (storageFileUpload) {
        if (!this.metadata) throw new Error(this.#translate.instant('ERROR.AUTHORISATION-DATA'));

        if (!this.extra?.urlPath || !this.extra?.storageZoneHostname) {
          this.toastr.danger('Missing Upload: urlPath or storageZoneHostname');
          continue;
        }
        storageFileUpload(file, this.metadata, this.extra.urlPath?.toString(), this.extra.storageZoneHostname?.toString());
      }

      // This is for stream video uploads, calls library videoToUploadQueue method, library deals with rest (auth, analytics, etc)
      if (videoFileUpload) videoFileUpload(file, undefined, this.existingItem as ExtendedVideo);
    }

    if (files?.length) this.bsModalRef.hide();
  }
}
